// GAVION ai follow-up (spec 2026-10-20-ai-follow-up-agent-design §1, §3, §5): the follow-up contract shared by the edge,
// the app (Paramètres → IA, the lead's « Suivi IA » panel) and the demo. Pure: no I/O, no remote import. SQL twins in
// 71_gavion_ai_follow_up.sql: private.ai_follow_up_defaults (DEFAULT_FOLLOW_UP, pinned by opsParity),
// private.ai_follow_up_angle (angleFor) and private.ai_follow_up_days (planTouchDays; the same date vectors run in
// ai_follow_up_plans.test.sql and followUp.test.ts).
import { AI_MODEL, AI_MODEL_SONNET, isAiModel, type AiModel } from "./types.ts";

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Obj) : {};

export const FOLLOW_UP_SITUATIONS = [
  "voicemail",
  "callback",
  "brochure",
  "demo",
] as const;
export type FollowUpSituation = (typeof FOLLOW_UP_SITUATIONS)[number];
/**
 * GAVION video-campaign: a plan's situation. 'campaign' (98_) is started by a sequence's last email, with the sequence's
 * own offsets (sequences.ai_follow_up_after): it has no cadence setting of its own.
 */
export type PlanSituation = FollowUpSituation | "campaign";
export const FOLLOW_UP_ANGLES = [
  "recap",
  "value",
  "proof",
  "question",
  "pre_callback",
  "breakup",
] as const;
export type FollowUpAngle = (typeof FOLLOW_UP_ANGLES)[number];
/** The touch being written (the tick's claimed row; the demo adds a fixture website detail). */
export type FollowUpFacts = {
  situation: PlanSituation;
  angle: FollowUpAngle;
  position: number;
  total: number;
  /** Demo only (fake drafter): a detail of the prospect's site from a fixture. */
  websiteDetail?: string | null;
};
/** The call outcome that plans each call situation (SQL: private.ai_follow_up_on_call); a demo plans from Granola. */
export const FOLLOW_UP_CALL_OUTCOMES: Readonly<
  Record<string, FollowUpSituation>
> = Object.freeze({
  voicemail: "voicemail",
  talked_callback: "callback",
  brochure_sent: "brochure",
});

export type FollowUpSettings = {
  enabled: boolean;
  demo: boolean;
  cadences: Record<FollowUpSituation, number[]>;
};
export const DEFAULT_FOLLOW_UP: FollowUpSettings = {
  enabled: true,
  demo: true,
  cadences: {
    voicemail: [1, 4, 9],
    brochure: [2, 6, 12],
    callback: [1],
    demo: [1, 5, 12],
  },
};
export const CADENCE_MAX_DAY = 60;
/** Touches per situation: 3 to 5; callback 1 to 5 (plus the automatic touch before a far callback). */
export const cadenceBounds = (
  s: FollowUpSituation,
): { min: number; max: number } =>
  s === "callback" ? { min: 1, max: 5 } : { min: 3, max: 5 };

export function validCadence(s: FollowUpSituation, v: unknown): v is number[] {
  if (!Array.isArray(v)) return false;
  const { min, max } = cadenceBounds(s);
  if (v.length < min || v.length > max) return false;
  return v.every(
    (n, i) =>
      Number.isInteger(n) &&
      n >= 1 &&
      n <= CADENCE_MAX_DAY &&
      (i === 0 || n > (v[i - 1] as number)),
  );
}
const bool = (v: unknown, d: boolean): boolean =>
  typeof v === "boolean" ? v : d;

/** gavion.ai.follow_up as the edge reads it (gavion_config().ai): each key falls back to its default (SQL twin). */
export function followUpSettingsFromAi(ai: unknown): FollowUpSettings {
  const f = obj(obj(ai).follow_up);
  const c = obj(f.cadences);
  const cadences = {} as Record<FollowUpSituation, number[]>;
  for (const s of FOLLOW_UP_SITUATIONS)
    cadences[s] = validCadence(s, c[s])
      ? [...(c[s] as number[])]
      : [...DEFAULT_FOLLOW_UP.cadences[s]];
  return {
    enabled: bool(f.enabled, DEFAULT_FOLLOW_UP.enabled),
    demo: bool(f.demo, DEFAULT_FOLLOW_UP.demo),
    cadences,
  };
}
/** The same from the whole configuration (configuration.config, as the app holds it). */
export const followUpSettingsOf = (config: unknown): FollowUpSettings =>
  followUpSettingsFromAi(obj(obj(config).gavion).ai);

export type ModelUse = "follow_up" | "drafts";
export const MODEL_USES: readonly ModelUse[] = ["follow_up", "drafts"];
export const DEFAULT_MODELS: Readonly<Record<ModelUse, AiModel>> =
  Object.freeze({ follow_up: AI_MODEL_SONNET, drafts: AI_MODEL });
/** gavion.ai.models: « Suivis » and « Brouillons » (on demand and R20–R23); an unknown model falls back. */
export function modelsFromAi(ai: unknown): Record<ModelUse, AiModel> {
  const m = obj(obj(ai).models);
  return {
    follow_up: isAiModel(m.follow_up) ? m.follow_up : DEFAULT_MODELS.follow_up,
    drafts: isAiModel(m.drafts) ? m.drafts : DEFAULT_MODELS.drafts,
  };
}
export const modelsOf = (config: unknown): Record<ModelUse, AiModel> =>
  modelsFromAi(obj(obj(config).gavion).ai);

/** The angle of a touch (SQL twin: private.ai_follow_up_angle). */
export function angleFor(
  s: FollowUpSituation,
  position: number,
  total: number,
  preCallback = false,
): FollowUpAngle {
  if (s === "callback")
    return position === 1 ? "recap" : preCallback ? "pre_callback" : "value";
  if (position === 1) return s === "demo" ? "recap" : "value";
  if (position === total && total >= 3) return "breakup";
  return (["proof", "question", "value"] as const)[(position - 2) % 3];
}

/**
 * GAVION video-campaign: the angle of a campaign touch (SQL twin: private.ai_follow_up_campaign_angle, 98_). Email 1
 * already pitched the value: proof, question, value…, the last of two or more being the breakup.
 */
export function campaignAngleFor(
  position: number,
  total: number,
): FollowUpAngle {
  if (position === total && total >= 2) return "breakup";
  return (["proof", "question", "value"] as const)[(position - 1) % 3];
}

const DAY_MS = 86_400_000;
const addDays = (d: string, n: number): string =>
  new Date(Date.parse(`${d}T00:00:00Z`) + n * DAY_MS)
    .toISOString()
    .slice(0, 10);
const isoDow = (d: string): number => {
  const w = new Date(`${d}T00:00:00Z`).getUTCDay();
  return w === 0 ? 7 : w;
};
/** Monday to Friday, not a holiday, not Dec 25 / Jan 1 (SQL: private.ai_follow_up_open_day). */
export const openDay = (d: string, holidays: ReadonlySet<string>): boolean =>
  isoDow(d) <= 5 && !holidays.has(d) && !/-(12-25|01-01)$/.test(d);
const nextOpen = (
  d: string,
  avoid: string | null,
  h: ReadonlySet<string>,
): string => {
  let x = d;
  while (!openDay(x, h) || x === avoid) x = addDays(x, 1);
  return x;
};
const addOpen = (from: string, n: number, h: ReadonlySet<string>): string => {
  let d = from;
  for (let k = 0; k < n; ) {
    d = addDays(d, 1);
    if (openDay(d, h)) k += 1;
  }
  return d;
};

/**
 * The touch days (YYYY-MM-DD, lead-local) of a plan started on `from` (SQL twin: private.ai_follow_up_days): each
 * offset in business days, never the callback day, each after the previous one; a callback plan places no touch after
 * its callback from the second touch on (the D+1 recap stays) and adds the day two business days before a callback
 * more than 7 days away when it comes after the last one (`preCallback`). A callback that is not YYYY-MM-DD is ignored.
 * Holidays outside the supplied `holidays` (e.g. a year not passed in) are treated as open days.
 */
export function planTouchDays(
  s: FollowUpSituation,
  from: string,
  callback: string | null,
  offsets: readonly number[],
  holidays: readonly string[],
): { days: string[]; preCallback: boolean } {
  const h = new Set(holidays);
  if (callback !== null && !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(callback))
    callback = null;
  const days: string[] = [];
  for (const k of offsets) {
    let d = nextOpen(addOpen(from, k, h), callback, h);
    const last = days[days.length - 1];
    if (last !== undefined && d <= last)
      d = nextOpen(addDays(last, 1), callback, h);
    // a callback plan places no touch after its callback from the second touch on (SQL: exit when … d > p_callback)
    if (s === "callback" && callback && days.length > 0 && d > callback) break;
    days.push(d);
  }
  let preCallback = false;
  if (
    s === "callback" &&
    callback &&
    (Date.parse(callback) - Date.parse(from)) / DAY_MS > 7
  ) {
    let d = callback;
    for (let k = 0; k < 2; ) {
      d = addDays(d, -1);
      if (openDay(d, h)) k += 1;
    }
    if (d > days[days.length - 1]) {
      days.push(d);
      preCallback = true;
    }
  }
  return { days, preCallback };
}
